import React, { useEffect, useState, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const createNumberedIcon = (number, color, isStart, isEnd) => {
  const bg = isStart ? '#16a34a' : isEnd ? '#dc2626' : color;
  const size = isStart || isEnd ? 32 : 26;
  const fontSize = isStart || isEnd ? 13 : 11;
  return L.divIcon({
    className: 'route-marker',
    html: `<div style="background:${bg};width:${size}px;height:${size}px;border-radius:50%;border:3px solid white;box-shadow:0 2px 10px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:${fontSize}px;font-family:-apple-system,sans-serif;">${number}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    tooltipAnchor: [size / 2 + 4, -4],
  });
};

const FitBounds = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    if (!coords || coords.length === 0) return;
    const fit = () => {
      try {
        map.invalidateSize({ animate: false });
        const bounds = L.latLngBounds(coords.map(c => L.latLng(c[0], c[1])));
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14, animate: false });
      } catch (e) { console.warn('fitBounds error:', e); }
    };
    fit();
    const t1 = setTimeout(fit, 200);
    const t2 = setTimeout(fit, 500);
    const t3 = setTimeout(fit, 1000);
    const t4 = setTimeout(fit, 2000);
    return () => { [t1, t2, t3, t4].forEach(clearTimeout); };
  }, [map, coords]);
  return null;
};

const AnimatedRoute = ({ positions, color, animate, duration }) => {
  const [drawn, setDrawn] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    if (!animate || positions.length < 2) { setDrawn(positions); return; }
    setDrawn([positions[0]]);
    let idx = 0;
    const step = duration / positions.length;
    ref.current = setInterval(() => {
      idx++;
      if (idx >= positions.length) { clearInterval(ref.current); setDrawn(positions); return; }
      setDrawn(prev => [...prev, positions[idx]]);
    }, step);
    return () => { if (ref.current) clearInterval(ref.current); };
  }, [positions, animate, duration]);

  if (drawn.length < 2) return null;
  return (
    <>
      <Polyline positions={drawn} pathOptions={{ color: '#1a1a6c', weight: 10, opacity: 0.12, lineCap: 'round', lineJoin: 'round' }} />
      <Polyline positions={drawn} pathOptions={{ color, weight: 5, opacity: 0.9, lineCap: 'round', lineJoin: 'round' }} />
      <Polyline positions={drawn} pathOptions={{ color: '#fff', weight: 1.5, opacity: 0.3, lineCap: 'round', lineJoin: 'round', dashArray: '6, 10' }} />
    </>
  );
};

const StopMarker = ({ stop, index, total, color }) => {
  const isStart = index === 0;
  const isEnd = index === total - 1;
  const icon = createNumberedIcon(index + 1, color, isStart, isEnd);
  return (
    <Marker position={stop.coordinates} icon={icon}>
      <Tooltip direction="top" offset={[0, -16]} className="route-tooltip">
        <div style={{ minWidth: 130, padding: '2px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <span style={{ background: isStart ? '#16a34a' : isEnd ? '#dc2626' : color, color: 'white', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{index + 1}</span>
            <strong style={{ fontSize: 12, color: '#1a1a1a' }}>{stop.name}</strong>
          </div>
          {stop.day && <div style={{ fontSize: 11, color: '#6B7280', marginLeft: 26 }}>Day {stop.day}</div>}
          {stop.description && <div style={{ fontSize: 10, color: '#9CA3AF', marginLeft: 26, marginTop: 1, maxWidth: 170 }}>{stop.description}</div>}
          {isStart && <div style={{ fontSize: 10, color: '#16a34a', fontWeight: 600, marginLeft: 26, marginTop: 1 }}>Starting Point</div>}
          {isEnd && <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 600, marginLeft: 26, marginTop: 1 }}>End Point</div>}
        </div>
      </Tooltip>
    </Marker>
  );
};

const RouteMap = ({
  routes = [],
  height = "400px",
  animate = true,
  animationDuration = 2500,
  className = ""
}) => {
  const allCoords = useMemo(() => routes.flatMap(r => (r.stops || []).map(s => s.coordinates)), [routes]);

  const center = useMemo(() => {
    if (allCoords.length === 0) return [20.5937, 78.9629];
    const lats = allCoords.map(c => c[0]);
    const lngs = allCoords.map(c => c[1]);
    return [(Math.min(...lats) + Math.max(...lats)) / 2, (Math.min(...lngs) + Math.max(...lngs)) / 2];
  }, [allCoords]);

  const mapKey = useMemo(() => allCoords.map(c => `${c[0]},${c[1]}`).join('|'), [allCoords]);

  const routeColors = ['#4338CA', '#7C3AED', '#2563EB', '#0891B2', '#6E5034'];

  return (
    <div className={`rounded-xl overflow-hidden border border-s-border shadow-lg ${className}`} style={{ height }} data-testid="route-map">
      <style>{`
        .route-tooltip { background: white !important; border: 1px solid #e5e7eb !important; border-radius: 10px !important; box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important; padding: 8px 10px !important; font-family: -apple-system, BlinkMacSystemFont, sans-serif !important; }
        .route-tooltip::before { border-top-color: white !important; }
        .route-marker { background: none !important; border: none !important; }
      `}</style>
      <MapContainer
        key={mapKey}
        center={center}
        zoom={8}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        zoomControl={true}
      >
        <FitBounds coords={allCoords} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
        />
        {routes.map((route, ri) => {
          const color = routeColors[ri % routeColors.length];
          const stops = route.stops || [];
          const coords = stops.map(s => s.coordinates);
          return (
            <React.Fragment key={ri}>
              {coords.length > 1 && <AnimatedRoute positions={coords} color={color} animate={animate} duration={animationDuration} />}
              {stops.map((stop, si) => (
                <StopMarker key={`${ri}-${si}`} stop={stop} index={si} total={stops.length} color={color} />
              ))}
            </React.Fragment>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default RouteMap;
